import { useLocalStorage, useMediaQuery, useTranslation } from "@opendash/core";
import { Icon } from "@opendash/icons";
import {
  Button,
  Col,
  Divider,
  Drawer,
  DrawerProps,
  Row,
  Select,
  Space,
  Switch,
} from "antd";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useEffect } from "react";
import { TicketManager } from "..";
import { fetchAllRolesWithSubordinates } from "../../../../_shared";
import { _User } from "../../../../js/parsegenerated";
import { TicketFilterSelect } from "./TicketFilterSelect";

type Props = {
  showGroupSwitch?: boolean;
  showCreatedUserSwitch?: boolean;
  showSubordinateSwitch?: boolean;
  sortManager?: TicketManager;
  filterManager?: TicketManager;
  userManager?: TicketManager;
  subordinateManager?: TicketManager;
  setOpen: (open: boolean) => void;
  open: boolean;
  refetch?: () => void;
};

export type FilterDrawerProps = DrawerProps & Props;

export const FilterDrawer = observer((props: FilterDrawerProps) => {
  const t = useTranslation();
  const isBelowWidth = useMediaQuery("(max-width: 576px)");
  const [subordinateFilter, setSubordinateFilter] = useLocalStorage<boolean>(
    "openservice:filter_subordinate"
  );

  useEffect(() => {
    if (props.subordinateManager && props.showSubordinateSwitch)
      manageSubordnateFilter(subordinateFilter);
  }, [subordinateFilter]);

  async function manageCreatedUserFilter(checked: boolean) {
    if (checked)
      props.userManager?.addFilter("createdUser", {
        attribute: "createdUser",
        value: Parse.User.current()?.id,
      });
    else props.userManager?.removeFilter("createdUser");

    props.refetch?.();
  }

  async function manageSubordnateFilter(checked: boolean) {
    const roles = await fetchAllRolesWithSubordinates(
      Parse.User.current() as _User
    );
    if (checked) {
      for (const role of roles) {
        props.subordinateManager?.addFilter(`subordinate-${role.id}`, {
          attribute: "role",
          value: role.id,
        });
      }
    } else {
      for (const role of roles) {
        props.subordinateManager?.removeFilter(`subordinate-${role.id}`);
      }
    }

    props.refetch?.();
  }

  return (
    <Drawer
      title={t("maintenance:settings.drawer.headline")}
      placement={"right"}
      width={isBelowWidth ? "100%" : "50%"}
      open={props.open}
      extra={
        <Space>
          <Button
            onClick={() => {
              props.filterManager?.resetFilters();
              props.refetch?.();
            }}
          >
            {t("maintenance:settings.button.reset.label")}
          </Button>
          <Button
            onClick={() => {
              props.setOpen(false);
            }}
          >
            {t("maintenance:global.button.close_label")}
          </Button>
        </Space>
      }
      onClose={() => {
        props.setOpen(false);
      }}
    >
      {props.showGroupSwitch && props.userManager && (
        <Row style={{ marginBottom: 10 }} gutter={8} justify="center">
          <Col span={24}>
            <Divider plain orientation="left">
              {t("maintenance:settings.displayGroupTickets.label")}
            </Divider>
          </Col>
          <Col>
            <Switch
              checked={!!props.userManager.getFilter("userRole")}
              onClick={(checked: boolean) => {
                if (checked) {
                  props.userManager.addFilter("userRole", {
                    attribute: "userRole",
                    value: Parse.User.current()?.id,
                  });
                } else {
                  props.userManager.removeFilter("userRole");
                }
                props.refetch?.();
              }}
            />
          </Col>
        </Row>
      )}

      {props.showSubordinateSwitch && props.subordinateManager && (
        <Row style={{ marginBottom: 10 }} gutter={8} justify="center">
          <Col span={24}>
            <Divider plain orientation="left">
              {t("maintenance:settings.displaySubordinateTickets.label")}
            </Divider>
          </Col>
          <Col>
            <Switch
              checked={subordinateFilter}
              onClick={(checked: boolean) => setSubordinateFilter(checked)}
            />
          </Col>
        </Row>
      )}

      {props.showCreatedUserSwitch && props.userManager && (
        <Row style={{ marginBottom: 10 }} gutter={8} justify="center">
          <Col span={24}>
            <Divider plain orientation="left">
              {t("maintenance:settings.displayCreatedUserTickets.label")}
            </Divider>
          </Col>
          <Col>
            <Switch
              checked={!!props.userManager.getFilter("createdUser")}
              onClick={manageCreatedUserFilter}
            />
          </Col>
        </Row>
      )}

      {props.sortManager && (
        <Row style={{ marginBottom: 10 }} gutter={8} justify="center">
          <Col span={24}>
            <Divider plain orientation="left">
              {t("maintenance:settings.sortby.label")}
            </Divider>
          </Col>
          <Col span={24}>
            <Row wrap={false}>
              <Select
                allowClear
                value={props.sortManager.getSortBy()}
                style={{ width: "100%" }}
                onChange={(value: string) => {
                  props.sortManager.setSortBy(value);
                  props.refetch?.();
                }}
                options={[
                  {
                    value: "createdAt",
                    label: t(
                      "maintenance:settings.sortby.options.ticket.createdAt"
                    ),
                  },
                  {
                    value: "title",
                    label: t(
                      "maintenance:settings.sortby.options.ticket.title"
                    ),
                  },
                  {
                    value: "latestPriorityValue",
                    label: t(
                      "maintenance:settings.sortby.options.priority.value"
                    ),
                  },
                ]}
              />

              <Button
                icon={
                  <Icon
                    icon={
                      props.sortManager.getOrder() === "asc"
                        ? "fa:sort-up"
                        : "fa:sort-down"
                    }
                  />
                }
                onClick={() => {
                  props.sortManager.toggleOrder();
                  props.refetch?.();
                }}
              />
            </Row>
          </Col>
        </Row>
      )}
      {props.filterManager && (
        <>
          <Divider plain orientation="left">
            {t("maintenance:settings.filterby.label")}
          </Divider>

          <TicketFilterSelect
            ticketManager={props.filterManager}
            refetch={props.refetch}
          />
        </>
      )}
    </Drawer>
  );
});
